import { Box, Flex, Skeleton, Text, VisuallyHidden } from '@chakra-ui/react';
import { OutOfStockTag } from '@olo-web/components/atoms/OutOfStockTag';
import { formatPrice } from '@olo-web/domain/orders/functions/formatPrice';
import { menuItemImageLoader } from '@olo-web/utils/common/functions';
import Image from 'next/image';
import React, { FC, memo, useMemo } from 'react';
import useMenuItemCard from './hooks';

interface MenuItemCardProps {
  item: IMenuItem;
  variant: string;
  groupName?: string;
  isLoading?: boolean;
}

const MenuItemCard: FC<MenuItemCardProps> = memo(({ item, variant, groupName, isLoading }) => {
  const { handleClick, styles, isMdDown, isSuccessAddedItem, setIsSuccessAddedItem } =
    useMenuItemCard({
      item,
      variant,
      groupName,
    });

  const hasPrice = Number(item?.price ?? 0) > 0;

  // Create descriptive aria-label for screen readers
  const ariaLabel = useMemo(() => {
    const itemName = item?.name ?? 'Menu item';
    const description = item?.description ? `, ${item?.description}` : '';
    const priceText = hasPrice ? `, ${formatPrice(item?.price)}` : '';
    return `${itemName}${description}${priceText}`;
  }, [item?.name, item?.description, item?.price, hasPrice]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (item.inStock) {
        handleClick();
      }
    }
  };

  if (isLoading) return <Skeleton w="100%" h="136px" mr={{ base: 0, sm: 6 }} />;

  return (
    <Flex
      {...styles.container}
      _hover={
        !isMdDown
          ? {
              cursor: !item.inStock ? 'not-allowed' : 'pointer',
              boxShadow: 'outline',
            }
          : 'none'
      }
      _focus={{
        outline: '2px solid',
        outlineColor: 'primary.350',
        outlineOffset: '2px',
      }}
      role={isSuccessAddedItem ? 'status' : 'button'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      justify="space-between"
      bg="white"
      data-testid="menu-item-card"
      onBlur={() => setIsSuccessAddedItem(false)}
      aria-disabled={!item.inStock}
      aria-label={ariaLabel}
    >
      {isSuccessAddedItem && (
        <VisuallyHidden role="status" aria-live="polite">
          Item added to order
        </VisuallyHidden>
      )}
      <Flex {...styles.textContainer}>
        <Flex flexDir="column">
          <Text {...styles?.name} data-testid="menu-item-card-name" as="h3">
            {item?.name}
          </Text>
          <Text {...styles.description} data-testid="menu-item-card-description" as="span">
            {item.description}
          </Text>
        </Flex>
        <Flex align="center" mt={{ base: 2, sm: 1 }}>
          <Text {...styles.price} data-testid="menu-item-card-price" as="span">
            {hasPrice ? formatPrice(item?.price) : ''}
          </Text>
          {!item.inStock && <OutOfStockTag ml={3} />}
        </Flex>
      </Flex>
      {item?.imageUrl && (
        <Box
          {...styles.image}
          data-testid="menu-item-card-image"
          opacity={!item.inStock ? 0.6 : 1}
          position="relative"
          aria-hidden="true"
        >
          <Image src={item.imageUrl} loader={menuItemImageLoader} fill objectFit="cover" alt="" />
        </Box>
      )}
    </Flex>
  );
});

export default MenuItemCard;
